import { useState, useMemo } from 'react';
import { Mail, AlertCircle, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

interface AuthFormProps extends React.ComponentProps<"div"> {}

type AuthMode = 'login' | 'register' | 'forgot-password';

// Password strength requirements
const PASSWORD_REQUIREMENTS = {
  minLength: { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  hasUppercase: { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  hasLowercase: { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  hasNumber: { label: 'One number', test: (p: string) => /[0-9]/.test(p) },
  hasSpecial: { label: 'One special character', test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
};

export function AuthForm({ className, ...props }: AuthFormProps) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [validationError, setValidationError] = useState<string>('');

  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstname: '',
    lastname: '',
  });

  const { login, register, isLoading, error, clearAuthError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Get the redirect path from location state, default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    if (!formData.password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    for (const requirement of Object.values(PASSWORD_REQUIREMENTS)) {
      if (requirement.test(formData.password)) strength++;
    }
    
    const total = Object.keys(PASSWORD_REQUIREMENTS).length;
    const percentage = (strength / total) * 100;
    
    if (percentage <= 20) return { strength: percentage, label: 'Very Weak', color: 'bg-red-500' };
    if (percentage <= 40) return { strength: percentage, label: 'Weak', color: 'bg-orange-500' };
    if (percentage <= 60) return { strength: percentage, label: 'Fair', color: 'bg-yellow-500' };
    if (percentage <= 80) return { strength: percentage, label: 'Strong', color: 'bg-blue-500' };
    return { strength: percentage, label: 'Very Strong', color: 'bg-green-500' };
  }, [formData.password]);

  // Check if form is complete for button disable state
  const isFormComplete = useMemo(() => {
    if (mode === 'login') {
      return formData.email.trim() !== '' && formData.password.trim() !== '';
    }
    if (mode === 'register') {
      return (
        formData.email.trim() !== '' &&
        formData.password.trim() !== '' &&
        formData.confirmPassword.trim() !== '' &&
        formData.firstname.trim() !== '' &&
        formData.lastname.trim() !== ''
      );
    }
    if (mode === 'forgot-password') {
      return formData.email.trim() !== '';
    }
    return false;
  }, [mode, formData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) clearAuthError(); // Clear error when user starts typing
    setValidationError(''); // Clear validation error when user types
  };

  const validateForm = (): boolean => {
    setValidationError(''); // Clear previous validation errors

    // For forgot-password mode, only validate email
    if (mode === 'forgot-password') {
      if (!formData.email) {
        setValidationError('Please enter your email address');
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setValidationError('Please enter a valid email address');
        return false;
      }
      return true;
    }

    // For login and register modes, validate email and password
    if (!formData.email) {
      setValidationError('Please enter your email address');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setValidationError('Please enter a valid email address');
      return false;
    }

    if (!formData.password) {
      setValidationError('Please enter your password');
      return false;
    }

    if (mode === 'register') {
      if (!formData.firstname) {
        setValidationError('Please enter your first name');
        return false;
      }
      if (!formData.lastname) {
        setValidationError('Please enter your last name');
        return false;
      }
      if (formData.password.length < 8) {
        setValidationError('Password must be at least 8 characters long');
        return false;
      }
      if (!formData.confirmPassword) {
        setValidationError('Please confirm your password');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setValidationError('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleForgotPassword = async (email: string) => {
    try {
      // First validate the email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error('Please enter a valid email address');
        return;
      }

      // Show loading state while processing
      toast.loading('Sending password reset instructions...', { id: 'forgot-password' });

      const adminApi = (await import('../services/adminApi')).default;
      const response = await adminApi.forgotPassword(email);
      
      toast.success(
        response.message || `Password reset instructions have been sent to ${email}. Please check your email and follow the instructions to reset your password.`,
        { 
          id: 'forgot-password',
          duration: 8000,
          style: {
            maxWidth: '500px',
          }
        }
      );

      setTimeout(() => {
        setMode('login');
      }, 3000);

    } catch (error: any) {
      console.error('Password reset error:', error);
      
      // Extract error message from different possible sources
      const errorMessage = 
        error?.message || 
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to send password reset instructions. Please try again or contact support if the problem persists.';
      
      toast.error(
        errorMessage,
        { 
          id: 'forgot-password',
          duration: 6000,
          style: {
            maxWidth: '500px',
          }
        }
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAuthError();

    if (!validateForm()) {
      return;
    }

    try {
      if (mode === 'login') {
        const success = await login({
          email: formData.email,
          password: formData.password
        });
        if (success) {
          navigate(from, { replace: true });
        }
      } else if (mode === 'register') {
        await register({
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          password: formData.password
        });
        // Redirect to OTP verification page after successful registration
        navigate('/verify-otp', { state: { email: formData.email } });
      } else if (mode === 'forgot-password') {
        await handleForgotPassword(formData.email);
      }
    } catch {
      // Errors are handled by the auth context
    }
  };

  const handleZohoLogin = async () => {
    try {
      // Import Zoho service dynamically
      const { ZohoOAuthService } = await import('../services/ZohoOAuthService');
      
      // Check if already authenticated
      if (ZohoOAuthService.isAuthenticated()) {
        const userProfile = await ZohoOAuthService.getUserProfile();
        
        // Try to login with Zoho email
        const success = await login({
          email: userProfile.Email,
          password: 'zoho-oauth-user' // This should be handled differently in production
        });
        if (success) {
          navigate(from, { replace: true });
        }
      } else {
        // Initiate OAuth flow
        ZohoOAuthService.initiateLogin();
      }
    } catch (err) {
      console.error('Zoho login error:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstname: '',
      lastname: '',
    });
    clearAuthError();
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    resetForm();
  };

  const getTitle = () => {
    switch (mode) {
      case 'login': return 'Welcome back';
      case 'register': return 'Create account';
      case 'forgot-password': return 'Reset password';
      default: return 'Welcome';
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'login': return 'Login to your Gandpittu account';
      case 'register': return 'Create your new account';
      case 'forgot-password': return 'Enter your email to receive a reset link';
      default: return '';
    }
  };

  const getSubmitText = () => {
    switch (mode) {
      case 'login': return 'Sign In';
      case 'register': return 'Create Account';
      case 'forgot-password': return 'Send Reset Link';
      default: return 'Submit';
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden border-none">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form onSubmit={handleSubmit} className="p-6 md:p-8 bg-white">
            <div className="flex flex-col gap-6">
              {/* Header */}
              <div className="flex flex-col items-center text-center">
               
                <h1 className="text-2xl font-bold">{getTitle()}</h1>
                <p className="text-balance text-muted-foreground">
                  {getSubtitle()}
                </p>
              </div>

              {/* Name fields for registration */}
              {mode === 'register' && (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="firstname">First Name</Label>
                    <Input
                      id="firstname"
                      type="text"
                      placeholder=""
                      value={formData.firstname}
                      onChange={(e) => handleInputChange('firstname', e.target.value)}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="lastname">Last Name</Label>
                    <Input
                      id="lastname"
                      type="text"
                      placeholder=""
                      value={formData.lastname}
                      onChange={(e) => handleInputChange('lastname', e.target.value)}
                      required
                    />
                  </div>
                </>
              )}

              {/* Email field */}
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>

              {/* Password field for login and register */}
              {mode !== 'forgot-password' && (
                <div className="grid gap-2">
                  <div className="flex items-center">
                    <Label htmlFor="password">Password</Label>
                    {mode === 'login' && (
                      <button
                        type="button"
                        onClick={() => switchMode('forgot-password')}
                        className="ml-auto text-sm underline-offset-2 hover:underline"
                      >
                        Forgot your password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      required
                      className="pr-10"
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {/* Password Strength Indicator - Only for registration */}
                  {mode === 'register' && formData.password && (
                    <div className="space-y-2 mt-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Password Strength:</span>
                        <span className={`font-medium ${
                          passwordStrength.strength <= 40 ? 'text-red-600' :
                          passwordStrength.strength <= 60 ? 'text-yellow-600' :
                          passwordStrength.strength <= 80 ? 'text-blue-600' : 'text-green-600'
                        }`}>
                          {passwordStrength.label}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                          style={{ width: `${passwordStrength.strength}%` }}
                        />
                      </div>

                      {/* Password Requirements Checklist */}
                      <div className="grid grid-cols-2 gap-1 mt-2">
                        {Object.entries(PASSWORD_REQUIREMENTS).map(([key, requirement]) => {
                          const isMet = requirement.test(formData.password);
                          return (
                            <div key={key} className="flex items-center gap-1.5 text-xs">
                              <CheckCircle2 className={`h-3 w-3 ${isMet ? 'text-green-500' : 'text-gray-300'}`} />
                              <span className={isMet ? 'text-green-700' : 'text-gray-500'}>
                                {requirement.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Confirm password field for registration */}
              {mode === 'register' && (
                <div className="grid gap-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      required
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      tabIndex={-1}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {/* Password Match Indicator */}
                  {formData.confirmPassword && formData.password && (
                    <div className="flex items-center gap-1.5 text-xs mt-1">
                      {formData.password === formData.confirmPassword ? (
                        <>
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                          <span className="text-green-700">Passwords match</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-3 w-3 text-red-500" />
                          <span className="text-red-700">Passwords do not match</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Error message */}
              {(error || validationError) && (
                <div className="bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 p-3 text-sm rounded-md flex items-center space-x-2">
                  <AlertCircle className="h-4 w-4" />
                  <span>{validationError || error?.message}</span>
                </div>
              )}

              {/* Submit button */}
              <Button type="submit" className="w-full bg-[#040707] hover:bg-opacity-50 hover:text-black text-white" disabled={isLoading || !isFormComplete}>
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Processing...
                  </div>
                ) : (
                  getSubmitText()
                )}
              </Button>

              {/* Social login options - only for login mode */}
              {mode === 'login' && (
                <>
                  <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center ">
                    <span className="relative z-10 bg-background px-2 text-muted-foreground">
                      Or continue with
                    </span>
                  </div>
                  
                  
                  {/* Zoho Login - Separate full-width button for better visibility */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleZohoLogin}
                    className="w-full"
                    disabled={isLoading}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Sign in with Zoho Mail
                  </Button>
                </>
              )}

              {/* Mode switching */}
              <div className="text-center text-sm">
                {mode === 'login' && (
                  <>
                    Don&apos;t have an account?{" "}
                    <button
                      type="button"
                      onClick={() => switchMode('register')}
                      className="underline underline-offset-4"
                    >
                      Sign up
                    </button>
                  </>
                )}
                {mode === 'register' && (
                  <>
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => switchMode('login')}
                      className="underline underline-offset-4"
                    >
                      Sign in
                    </button>
                  </>
                )}
                {mode === 'forgot-password' && (
                  <>
                    Remember your password?{" "}
                    <button
                      type="button"
                      onClick={() => switchMode('login')}
                      className="underline underline-offset-4"
                    >
                      Back to login
                    </button>
                  </>
                )}
              </div>
            </div>
          </form>

          {/* Right side image */}
          <div className="relative hidden bg-muted md:block !bg-[#040707]">
            <img
              src="/logo.webp"
              alt="Grand Pittu Restaurant Logo"
              className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
            />
          </div>
        </CardContent>
      </Card>

      {/* Terms and privacy
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
        By clicking continue, you agree to our <a href="#">Terms of Service</a>{" "}
        and <a href="#">Privacy Policy</a>.
      </div> */}
    </div>
  );
}
